import { useState, useEffect } from "react"
import "./WhyNeeds.css"; // Import the CSS file
const DigitalMarketingSection = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [activePoint, setActivePoint] = useState(0)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [wavePhase, setWavePhase] = useState(0)

  useEffect(() => {
    setIsVisible(true)
    
    const interval = setInterval(() => {
      setActivePoint((prev) => (prev + 1) % 5)
    }, 3000)

    const waveInterval = setInterval(() => {
      setWavePhase(prev => prev + 1)
    }, 100)

    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect()
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100
      })
    }

    // Add event listener to the container div reference
    const container = document.querySelector('.position-relative.overflow-hidden');
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      clearInterval(interval)
      clearInterval(waveInterval)
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [])

  const marketingPoints = [
    {
      icon: "🔍",
      stat: "80%",
      title: "Online Research First",
      description: "of consumers search online before making a purchase decision. If you're not showing up, you're missing sales.",
      color: "#ff6b6b",
      angle: 0
    },
    {
      icon: "📱",
      stat: "3x",
      title: "Social Media Power", 
      description: "more brand awareness through social media marketing compared to traditional advertising methods.",
      color: "#4ecdc4",
      angle: 72
    },
    {
      icon: "🎯",
      stat: "100%",
      title: "Cost-Effective Targeting",
      description: "reach the exact audience who's interested in your product, without wasting money on irrelevant ads.",
      color: "#45b7d1",
      angle: 144
    },
    {
      icon: "📊",
      stat: "Real-time",
      title: "Trackable Results",
      description: "measure every click, lead, and conversion to refine your strategy and maximize ROI.",
      color: "#96ceb4",
      angle: 216
    },
    {
      icon: "⚡",
      stat: "NOW",
      title: "Competitive Advantage", 
      description: "Your competitors are already online — staying offline means handing over customers to them.",
      color: "#ffeaa7",
      angle: 288
    }
  ]

  return (
    <div
      className="position-relative overflow-hidden d-flex align-items-center"
      style={{
        background: 'white',
        minHeight: "100vh",
        padding: "50px 0",
        gap: '30px' // Fixed: Removed semicolon
      }}
    >
      {/* Dynamic Wave Background */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          background: `
            radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, rgba(255,107,107,0.1) 0%, transparent 50%),
            radial-gradient(circle at ${100-mousePos.x}% ${100-mousePos.y}%, rgba(78,205,196,0.1) 0%, transparent 50%)
          `,
          transition: 'background 0.3s ease'
        }}
      />

      {/* Flowing Lines */}
      <svg 
        className="position-absolute w-100 h-100" 
        style={{ top: 0, left: 0, zIndex: 1 }}
        viewBox="0 0 1000 1000"
      >
        {[...Array(3)].map((_, i) => (
          <path
            key={i}
            d={`M0,${300 + i * 200} Q250,${250 + i * 200 + Math.sin(wavePhase * 0.1 + i) * 50} 500,${300 + i * 200} T1000,${300 + i * 200}`}
            fill="none"
            stroke={i === 0 ? '#ff6b6b20' : i === 1 ? '#4ecdc420' : '#45b7d120'}
            strokeWidth="2"
            opacity="0.6"
            style={{
              filter: 'blur(1px)',
              animation: `flowLine ${8 + i * 2}s ease-in-out infinite alternate`
            }}
          />
        ))}
      </svg>

      <div className="container" style={{ position: "relative", zIndex: 2 }}>
        <div className="row align-items-center min-vh-100">
          
          {/* Left Side - Header Content */}
          <div className="col-lg-5 col-md-12 mb-5 mb-lg-0">
            <div
              style={{
                transform: isVisible ? "translateX(0)" : "translateX(-100px)",
                opacity: isVisible ? 1 : 0,
                transition: "all 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                className="d-inline-block mb-4 px-4 py-2"
                style={{
                  background: "linear-gradient(135deg, #ff6b6b15, #4ecdc415)",
                  borderRadius: "50px",
                  border: "2px solid rgba(0,0,0,0.1)",
                  transform: `rotate(${Math.sin(wavePhase * 0.05) * 3}deg)`
                }}
              >
                <span style={{ fontSize: "1.8rem", marginRight: "10px" }}>📈</span>
                <span style={{ color: "black", fontWeight: "700", fontSize: "0.9rem", letterSpacing: "1px" }}>
                  DIGITAL REVOLUTION
                </span>
              </div>

              <h1
                className="display-3 fw-bold mb-4"
                style={{
                  color: "black",
                  lineHeight: "1.1",
                  textShadow: "0 0 20px rgba(0,0,0,0.1)"
                }}
              >
                Why Your Business{" "}
                <div
                  style={{
                    display: "inline-block",
                    background: "linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)",
                    backgroundSize: "300% 300%",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    animation: "gradientShift 4s ease-in-out infinite"
                  }}
                >
                  Needs Digital Marketing
                </div>{" "}
                Now More Than Ever
              </h1>

              <p
                className="lead mb-4"
                style={{
                  color: "rgba(0,0,0,0.7)",
                  fontSize: "1.3rem",
                  lineHeight: "1.7"
                }}
              >
                In today's fast-paced world, your audience is online — and if you're not there,{" "}
                <span 
                  style={{ 
                    color: "#ff6b6b", 
                    fontWeight: "700",
                    textShadow: "0 0 10px rgba(255,107,107,0.3)"
                  }}
                >
                  you're invisible
                </span>.
              </p>

              <div
                className="p-4 mb-4"
                style={{
                  background: "linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 100%)",
                  borderRadius: "20px",
                  borderLeft: "5px solid #ff6b6b",
                  transform: `translateX(${Math.sin(wavePhase * 0.03) * 5}px)`
                }}
              >
                <p
                  className="mb-0 fw-bold"
                  style={{
                    color: "black",
                    fontSize: "1.2rem"
                  }}
                >
                  Digital marketing is no longer optional — it's a{" "}
                  <span
                    style={{
                      background: "linear-gradient(135deg, #ff6b6b, #4ecdc4)",
                      WebkitBackgroundClip: "text", 
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      fontSize: "1.4rem",
                      fontWeight: "900"
                    }}
                  >
                    MUST-HAVE
                  </span>{" "}
                  for every business
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Circular Orbit Design */}
          <div className="col-lg-7 col-md-12">
            <div
              className="position-relative d-flex align-items-center justify-content-center"
              style={{
                height: "600px",
                transform: isVisible ? "scale(1)" : "scale(0.5)",
                opacity: isVisible ? 1 : 0,
                transition: "all 1.5s cubic-bezier(0.4, 0, 0.2, 1) 0.5s",
              }}
            >
              
              {/* Central Hub */}
              <div
                className="position-absolute d-flex align-items-center justify-content-center"
                style={{
                  width: "160px",
                  height: "160px",
                  background: "linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1)",
                  borderRadius: "50%",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                  animation: "centerPulse 3s ease-in-out infinite",
                  zIndex: 10
                }}
              >
                <div
                  className="d-flex flex-column align-items-center justify-content-center text-white"
                  style={{
                    width: "140px",
                    height: "140px",
                    background: "rgba(255,255,255,0.9)",
                    borderRadius: "50%",
                    backdropFilter: "blur(20px)"
                  }}
                >
                  <div style={{ fontSize: "3rem", marginBottom: "5px" }}>🚀</div>
                  <div style={{ color: "black", fontWeight: "800", fontSize: "0.9rem", textAlign: "center" }}>
                    YOUR BUSINESS
                  </div>
                </div>
              </div>

              {/* Orbiting Points */}
              {marketingPoints.map((point, index) => {
                const radius = 250
                const angle = (point.angle + wavePhase * 2) * (Math.PI / 180)
                const x = Math.cos(angle) * radius
                const y = Math.sin(angle) * radius
                const isActive = activePoint === index

                return (
                  <div key={index}>
                    {/* Orbit Line */}
                    <div
                      className="position-absolute"
                      style={{
                        width: "2px",
                        height: `${radius}px`,
                        background: `linear-gradient(to bottom, ${point.color}40, transparent)`,
                        transformOrigin: "bottom center",
                        transform: `rotate(${point.angle + wavePhase * 2}deg)`,
                        left: "50%",
                        top: "50%",
                        marginLeft: "-1px",
                        marginTop: `-${radius}px`,
                        opacity: isActive ? 1 : 0.3,
                        transition: "opacity 0.5s ease"
                      }}
                    />

                    {/* Orbiting Point */}
                    <div
                      className="position-absolute"
                      style={{
                        left: "50%",
                        top: "50%",
                        marginLeft: `${x - 60}px`,
                        marginTop: `${y - 60}px`,
                        width: "120px",
                        height: "120px",
                        zIndex: 5
                      }}
                      onMouseEnter={() => setActivePoint(index)}
                    >
                      <div
                        className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center position-relative"
                        style={{
                          background: isActive 
                            ? `linear-gradient(135deg, ${point.color}, ${point.color}cc)`
                            : `${point.color}20`,
                          borderRadius: "25px",
                          border: `3px solid ${isActive ? point.color : point.color + '40'}`,
                          cursor: "pointer",
                          transform: isActive ? "scale(1.2)" : "scale(1)",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: isActive 
                            ? `0 20px 40px ${point.color}50, 0 0 30px ${point.color}30`
                            : `0 10px 20px rgba(0,0,0,0.1)`,
                          backdropFilter: "blur(15px)"
                        }}
                      >
                        <div
                          style={{
                            fontSize: isActive ? "2.5rem" : "2rem",
                            marginBottom: "5px",
                            transform: `rotate(${isActive ? Math.sin(wavePhase * 0.1) * 10 : 0}deg)`,
                            transition: "all 0.3s ease"
                          }}
                        >
                          {point.icon}
                        </div>
                        <div
                          style={{
                            color: isActive ? "white" : "black",
                            fontWeight: "900",
                            fontSize: isActive ? "1.1rem" : "0.9rem",
                            transition: "all 0.3s ease",
                            textShadow: isActive ? "0 2px 10px rgba(0,0,0,0.3)" : "none"
                          }}
                        >
                          {point.stat}
                        </div>

                        {/* Floating Description */}
                        {isActive && (
                          <div
                            className="position-absolute"
                            style={{
                              bottom: "-80px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: "200px",
                              background: "rgba(255,255,255,0.95)",
                              padding: "15px",
                              borderRadius: "15px",
                              border: `2px solid ${point.color}`,
                              boxShadow: `0 15px 35px rgba(0,0,0,0.2)`,
                              zIndex: 20,
                              animation: "fadeInUp 0.3s ease-out"
                            }}
                          >
                            <h6 style={{ color: point.color, fontWeight: "700", marginBottom: "8px", fontSize: "0.9rem" }}>
                              {point.title}
                            </h6>
                            <p style={{ color: "rgba(0,0,0,0.8)", fontSize: "0.8rem", margin: 0, lineHeight: "1.4" }}>
                              {point.description}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Connection Lines Between Points */}
              <svg 
                className="position-absolute w-100 h-100"
                style={{ top: 0, left: 0, pointerEvents: "none" }}
                viewBox="0 0 600 600"
              >
                {marketingPoints.map((_, index) => {
                  if (index === marketingPoints.length - 1) return null
                  const nextIndex = (index + 1) % marketingPoints.length
                  const isConnectionActive = activePoint === index || activePoint === nextIndex
                  
                  return (
                    <circle
                      key={index}
                      cx="300"
                      cy="300"
                      r="250"
                      fill="none"
                      stroke={isConnectionActive ? '#ff6b6b40' : 'rgba(0,0,0,0.1)'}
                      strokeWidth="1"
                      strokeDasharray="5,5"
                      opacity={isConnectionActive ? 1 : 0.3}
                      style={{
                        transition: "all 0.5s ease",
                        animation: isConnectionActive ? "dashMove 2s linear infinite" : "none"
                      }}
                    />
                  )
                })}
              </svg>
            </div>
          </div>
        </div>

        
      </div>
    </div>
  )
}

export default DigitalMarketingSection 